import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import queryString from "query-string";

import { Select } from "../ProviderSearchBox";
import { LoadingIndicator } from "../LoadingIndicator";
import {
    buildUri,
    EMPTY_ARRAY,
    FilterMetadataApiWithCountReturn,
    PROVIDER_CONNECTION_TYPE,
    ROUTE,
    ROUTE_TITLE,
    tracker,
    WaterProvider
} from "../misc";
import { useMetadataAndProviderLoader } from "./useProviderInfoPage";
import { Tab, TabContent, TabData } from "../Container";

import { CloseButton, StdButton } from "../../Components/StdButton";
import styles from "./ProviderCompareSelectPage.module.scss";
import { useReactBlockPageFromRoute } from "../../Components/hooks";
import { Paragraph } from "../Paragraph";
import { fetchProviders } from "../misc/fetchProviders";

import _ from "lodash";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faGreaterThan, faLessThan } from "@fortawesome/pro-solid-svg-icons";
import { formatNumber } from "../../utils";

export type SelectContext = "from" | "to";

interface ProviderCompareSelectPageUrlParams {
    from?: string;
    to?: string;
}

interface LProviderCompareSelectPageProps {
    adminId: string;
    urlParams: {
        from: string;
        to?: string;
    };
}

export type ComparisonSelectTabValue = "size" | "type" | "region";

export interface ComparisonSelectTabDefinition extends TabData {
    id: ComparisonSelectTabValue;
}

export const ComparisonSelectTab: React.FC<{}> = () => {
    const tabData = React.useMemo<ComparisonSelectTabDefinition[]>(() => {
        return [
            {
                id: "size",
                title: "Compare by size",
                shortTitle: "Compare by size"
            },
            {
                id: "type",
                title: "Compare by type",
                shortTitle: "Compare by type"
            },
            {
                id: "region",
                title: "Compare by region",
                shortTitle: "Compare by region"
            }
        ];
    }, []);

    return (
        <Tab tabData={tabData} className={styles.compareSelectTab}>
            <TabContent id="size">
                <div className={`container`}>size</div>
            </TabContent>
            <TabContent id="type">
                <div className={`container`}>type</div>
            </TabContent>
            <TabContent id="region">
                <div className={`container`}>region</div>
            </TabContent>
        </Tab>
    );
};

export interface ListItem {
    id: string;
    name: string;
}

export interface CompareEntitySelectProps {
    className?: string;
    placeholder: string;
    options: ListItem[];
    option?: ListItem;
    onChange: (option: ListItem) => void;
}
export const CompareEntitySelect: React.FC<CompareEntitySelectProps> = ({
    className,
    options,
    option,
    onChange,
    placeholder
}) => {
    const _options = useMemo(() => {
        return options.map(d => {
            return {
                label: d.name,
                topic: d.id,
                value: d.id
            };
        });
    }, [options]);

    const _option = useMemo(() => {
        return !option ? null : _options.find(d => d.value === option.id);
    }, [option, _options]);

    return (
        <Select
            className={className ?? ""}
            options={_options}
            value={_option}
            placeholder={placeholder}
            // themeColor={scss["blue1"] as string}
            focusBorderWidth={1.5}
            notFocusBorderWidth={1.5}
            onChange={option => {
                onChange && onChange(options.find(d => d.id === option.value));
            }}
        />
    );
};

export const SimilarProviderToSelect: React.FC<{
    provider: WaterProvider;
    providers: WaterProvider[];
    // onSelectProvider: () => {};
    metadata: FilterMetadataApiWithCountReturn;

    type: "connectionSizeCategory" | "type" | "region";
}> = ({ provider, providers, type, metadata }) => {
    const history = useHistory();

    const [selectedProvider, setSelectedProvider] = useState<WaterProvider>(null);
    const filteredProviders: WaterProvider[] = useMemo(() => {
        if (!provider || !providers) return EMPTY_ARRAY;

        return _.orderBy(
            providers.filter(d => d[type] === provider[type] && d.tid !== provider.tid),
            d =>
                type === "connectionSizeCategory"
                    ? d.connectionSize
                    : type === "type"
                    ? d.name
                    : type === "region"
                    ? d.name
                    : "",
            "asc"
        );
    }, [provider, providers, type]);

    const restProviders: WaterProvider[] = useMemo(() => {
        if (!providers) return EMPTY_ARRAY;

        const idToFilteredProviders = _.keyBy(filteredProviders ?? EMPTY_ARRAY, d => d.tid);

        // providers = providers?.filter(d => d.connectionSizeCategory !== PROVIDER_CONNECTION_TYPE.bulk);

        return _.orderBy(
            provider?.connectionSizeCategory === PROVIDER_CONNECTION_TYPE.bulk
                ? (EMPTY_ARRAY as WaterProvider[])
                : providers.filter(pr => {
                      return (
                          !idToFilteredProviders[pr.tid] &&
                          pr.tid !== provider.tid &&
                          pr.connectionSizeCategory !== PROVIDER_CONNECTION_TYPE.bulk
                      );
                  }),
            d => d.name,
            "asc"
        );
    }, [filteredProviders, providers, provider]);

    const options: { id: string; name: string }[] = useMemo(() => {
        return (
            restProviders?.map(pr => {
                return {
                    id: pr.adminId,
                    name: pr.name
                };
            }) ?? EMPTY_ARRAY
        );
    }, [restProviders]);

    const option: { id: string; name: string } = useMemo(() => {
        return options?.find(pr => {
            return pr.id === selectedProvider?.adminId;
        });
    }, [options, selectedProvider]);

    const typeLabel = useMemo(() => {
        return provider?.[type] === PROVIDER_CONNECTION_TYPE.bulk
            ? "Bulk water"
            : metadata?.[
                  type === "connectionSizeCategory"
                      ? "providerSize"
                      : type === "region"
                      ? "providerRegion"
                      : type === "type"
                      ? "providerType"
                      : ""
              ]?.options?.find(d => d.id === provider?.[type])?.label;
    }, [metadata, provider, type]);

    const typeExtra = useMemo(() => {
        if (type !== "connectionSizeCategory") return "";

        const rangeInfo = metadata?.["providerSize"]?.options?.find(d => d.id === provider?.[type]);

        const start = rangeInfo?.queryRangeStart;
        const end = rangeInfo?.queryRangeEnd;

        if (start == null && end == null) return "";

        if (start == null && end != null)
            return (
                <>
                    (<FontAwesomeIcon icon={faLessThan} />
                    {formatNumber(end)} connections)
                </>
            );

        if (start != null && end == null)
            return (
                <>
                    (<FontAwesomeIcon icon={faGreaterThan} />
                    {formatNumber(start)} connections)
                </>
            );

        if (start != null && end != null)
            return (
                <>
                    ({formatNumber(start)} - {formatNumber(end)} connections)
                </>
            );
    }, [metadata, provider, type]);

    return (
        <>
            <div className={styles.typeLabel}>
                <span>{typeLabel}</span> {` `}
                <span className={styles.typeLabelExtra}>{typeExtra}</span>
            </div>

            <div className={`row ${styles.providerListContainer}`}>
                <div className={`col-md-4 col-12 ${styles.providerList}`}>
                    {filteredProviders.map((pr, index) => {
                        return (
                            <div
                                key={index}
                                className={`${styles.providerItem} ${
                                    pr.tid === selectedProvider?.tid ? styles.selected : ""
                                }`}
                                onClick={() => {
                                    setSelectedProvider(pr);
                                }}
                            >
                                <div className={styles.selectedIcon}>
                                    <FontAwesomeIcon icon={faCircleCheck} />
                                </div>
                                <div className={styles.providerItemContent}>
                                    {pr.name} {` `}
                                    {type === "connectionSizeCategory" &&
                                        pr.connectionSizeCategory !== PROVIDER_CONNECTION_TYPE.bulk && (
                                            <span className={styles.mobileDisplay}>
                                                &nbsp;{`(${formatNumber(pr.connectionSize)})`}
                                            </span>
                                        )}
                                </div>
                                {type === "connectionSizeCategory" &&
                                    pr.connectionSizeCategory !== PROVIDER_CONNECTION_TYPE.bulk && (
                                        <span className={styles.mobileNotDisplay}>
                                            &nbsp;{`(${formatNumber(pr.connectionSize)})`}
                                        </span>
                                    )}
                            </div>
                        );
                    })}
                </div>
                <div className={`col-md-8 col-12 ${styles.disclaimerContainer}`}>
                    <div className={`${styles.disclaimer}`}>
                        <div className={`${styles.disclaimerText}`}>
                            Disclaimer:{" "}
                            <strong>
                                By selecting a provider to compare and clicking the button below, you acknowledge that
                                you understand that providers are different in many ways and are not always comparable.
                            </strong>
                        </div>

                        <div>
                            <StdButton
                                variant="std"
                                disabled={selectedProvider == null || provider == null}
                                onClick={() => {
                                    const uri = buildUri(
                                        ROUTE.PROVIDER_COMPARE,
                                        {},
                                        {
                                            from: provider.adminId,
                                            to: selectedProvider.adminId
                                        }
                                    );

                                    selectedProvider?.adminId != null && history.push(uri);
                                }}
                            >
                                Compare selected
                            </StdButton>
                        </div>
                    </div>
                </div>
            </div>
            {options?.length > 0 && (
                <>
                    <h4>All other providers</h4>
                    <CompareEntitySelect
                        placeholder="Select a provider"
                        options={options}
                        className="double-margin-bottom col-md-4 col-12 p-0"
                        option={option}
                        onChange={item => {
                            item != null && setSelectedProvider(providers?.find(d => d.adminId === item.id));
                        }}
                    />
                </>
            )}
        </>
    );
};

// const WARNING_MESSAGE = (
//     <span>
//         Queensland’s urban water sector is diverse. Some service providers should not be compared and there are many
//         reasons why their performance can vary. Each service provider delivers this essential water and sewerage
//         services to suit its customers, location, size, climate and regulatory requirements, and therefore may not be
//         directly comparable as ‘like-for-like’.
//     </span>
//     // </ReadMore>
//     // <>
//     //     <p>
//     //         Water bills can vary based on the service provider’s tariff or pricing structure and the volume of water
//     //         used by customers. Therefore, water bills may not be directly comparable.&nbsp;
//     //     </p>
//     //     <p>
//     //         A service provider’s water and sewerage charges reflect the costs associated with delivering water to homes
//     //         and businesses and disposing of sewage.&nbsp;
//     //     </p>
//     //     <div className="readmore-anchor">&nbsp;</div>
//     //     <p>
//     //         Costs to deliver these services are impacted by location, climate conditions, number of customers, density
//     //         of the area serviced, water and sewage treatment requirements, bulk water charges, infrastructure age and
//     //         condition, weather events, customer demand, operating model.
//     //     </p>
//     // </>
// );
export const LProviderCompareSelectPage: React.FC<LProviderCompareSelectPageProps> = ({ adminId, urlParams }) => {
    const history = useHistory();
    const { data: reactBlockData, loading: reactBlockLoading } = useReactBlockPageFromRoute();
    const [providers, setProviders] = useState<WaterProvider[]>(null);

    // const [selectedProvider, setSelectedProvider] = useState<WaterProvider>(null);

    const paragraphs = reactBlockData?.result?.map(d => d.body)?.filter(d => d != null) ?? EMPTY_ARRAY;

    const { provider, loading, metadata, metadataLoading } = useMetadataAndProviderLoader(adminId);

    const loadProviders: () => Promise<void> = useCallback(async () => {
        try {
            let providers = await fetchProviders();
            // providers = providers?.filter(d => d.connectionSizeCategory !== PROVIDER_CONNECTION_TYPE.bulk);
            setProviders(providers);
        } catch (err) {
            console.log(err);
        }
    }, []);

    useEffect(() => {
        loadProviders();
    }, [loadProviders]);

    const tabData = React.useMemo<ComparisonSelectTabDefinition[]>(() => {
        return [
            {
                id: "size",
                title: "Compare by size",
                shortTitle: "Compare by size"
            },
            {
                id: "type",
                title: "Compare by type",
                shortTitle: "Compare by type"
            },
            {
                id: "region",
                title: "Compare by region",
                shortTitle: "Compare by region"
            }
        ];
    }, []);

    const defaultContent = (
        <div className="row">
            <div className="col-12">
                <LoadingIndicator />
            </div>
        </div>
    );

    const _loading =
        providers == null || loading == null || loading === true || metadataLoading == null || metadataLoading === true;

    if (_loading) return defaultContent;

    if (!provider || !metadata) return null;

    const closeButton = (
        <CloseButton
            className="double-margin-bottom"
            onClick={() => {
                const hasFrom = urlParams?.from != null && urlParams?.from.trim() !== "";
                const hasTo = urlParams?.to != null && urlParams?.to.trim() !== "";
                history.push(
                    buildUri(
                        hasFrom && hasTo ? ROUTE.PROVIDER_COMPARE : hasFrom ? ROUTE.PROVIDER_INFO : ROUTE.SEARCH_PAGE,
                        {},
                        hasFrom && hasTo
                            ? urlParams
                            : hasFrom
                            ? {
                                  id: urlParams?.from
                              }
                            : {}
                    )
                );
            }}
        ></CloseButton>
    );

    return (
        <>
            {closeButton}
            <h2>Select a provider to compare with {provider?.name ?? "...Loading"}</h2>

            {/* <AttentionBox variant="compareWarning" readMore={true} message={WARNING_MESSAGE} maxHeight={150} /> */}

            {(reactBlockLoading || reactBlockData === undefined) && <LoadingIndicator />}
            {!reactBlockLoading && (
                <div className={`${styles.compareInfo}`}>
                    {paragraphs.map((paragraph, index) => {
                        return (
                            <Paragraph
                                key={index}
                                nid={-1}
                                status={true}
                                title={undefined}
                                adminId={"-1"}
                                text={paragraph}
                            />
                        );
                    })}
                </div>
            )}
            {provider?.connectionSizeCategory === PROVIDER_CONNECTION_TYPE.bulk && (
                <>
                    <div>
                        <SimilarProviderToSelect
                            metadata={metadata}
                            provider={provider}
                            providers={providers}
                            type="connectionSizeCategory"
                        />
                    </div>
                </>
            )}
            {provider?.connectionSizeCategory !== PROVIDER_CONNECTION_TYPE.bulk && (
                <Tab tabData={tabData} className={styles.compareSelectTab}>
                    <TabContent id="size">
                        <div>
                            <SimilarProviderToSelect
                                metadata={metadata}
                                provider={provider}
                                providers={providers}
                                type="connectionSizeCategory"
                            />
                        </div>
                    </TabContent>
                    <TabContent id="type">
                        <div>
                            <SimilarProviderToSelect
                                metadata={metadata}
                                provider={provider}
                                providers={providers}
                                type="type"
                            />
                        </div>
                    </TabContent>
                    <TabContent id="region">
                        <div>
                            <SimilarProviderToSelect
                                metadata={metadata}
                                provider={provider}
                                providers={providers}
                                type="region"
                            />
                        </div>
                    </TabContent>
                </Tab>
            )}

            {/* <ProviderFinder
                className="double-margin-bottom col-md-6 col-12 p-0"
                provider={provider}
                onChange={providerSearchResult => {
                    const uri = buildUri(
                        ROUTE.PROVIDER_COMPARE,
                        {},
                        {
                            from: provider.adminId,
                            to: providerSearchResult?.provider?.adminId
                        }
                    );

                    providerSearchResult?.provider?.adminId != null && history.push(uri);
                }}
            /> */}

            {closeButton}
        </>
    );
};

const useProviderCompareSelectPageRouteProtector = () => {
    const history = useHistory();
    const { search } = useLocation();
    if (search == null || search.toString().trim() === "") history.push(ROUTE.SEARCH_PAGE);

    const params =
        search == null
            ? ({} as ProviderCompareSelectPageUrlParams)
            : (queryString.parse(search) as ProviderCompareSelectPageUrlParams);

    const { from, to } = params ?? {};

    if (
        from == null ||
        from.toString().trim() === ""
        //((from == null || from.toString().trim() === "") && (to == null || to.toString().trim() === "")) ||
        //(from != null && from.toString().trim() !== "" && to != null && to.toString().trim() !== "")
    )
        history.push(ROUTE.SEARCH_PAGE);

    const adminId = from;
    // const context: SelectContext = from != null && from.toString().trim() !== "" ? "from" : "to";

    return {
        adminId,
        urlParams: {
            from,
            to
        }
    };
};

export const ProviderCompareSelectPage: React.FC<{}> = () => {
    const { adminId, urlParams } = useProviderCompareSelectPageRouteProtector();
    document.title = ROUTE_TITLE.PROVIDER_SELECT_TO_COMPARE;
    tracker.providerPageNavHistoryIndex = buildUri(ROUTE.PROVIDER_SELECT_TO_COMPARE, {}, urlParams);
    return (
        <>
            <LProviderCompareSelectPage urlParams={urlParams} adminId={adminId} />
        </>
    );
};
